"use client";

import { motion, useScroll, useTransform, type MotionValue } from "framer-motion";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { GridBorderedSection, GridDivider } from "./GridLayout";

const FEATURES = [
  {
    title: "Sealed Sandboxed Containers",
    description:
      "No state leaks between runs. Strict version pinning and audit controls guarantee the same code runs the same way, always.",
  },
  {
    title: "Self-Healing Execution",
    description:
      "If a step fails, the engine doesn\u2019t stop \u2014 it retries, reroutes, and finds an alternative path automatically. Your flow reaches its destination or tells you exactly why it couldn\u2019t.",
  },
  {
    title: "Real-Time Command Log",
    description:
      "Every action is logged step-by-step, in real time. Watch flows execute live, replay any run, or surface logs directly in the chatbot interface. No black boxes.",
  },
  {
    title: "Crypto-Native Context",
    description:
      "B3OS holds deep operational context across blockchains, wallets, and DeFi protocols. Flows aren\u2019t just blockchain-aware \u2014 they can reason and act onchain natively.",
  },
  {
    title: "Secure Payments Logic",
    description:
      "Payment flows are pre-audited and compliant. Built-in wallet management, role-based permissions, and safeguards ensure funds only move when \u2014 and exactly how \u2014 you specify.",
  },
  {
    title: "Deterministic Output",
    description:
      "The same inputs produce the same outputs. Every time. This isn\u2019t a feature \u2014 it\u2019s the architectural guarantee everything else is built on. Scale with confidence.",
  },
];

const CARD_LAYERS = [
  { src: "/images/homepage/reliable/card-b3os.png", topPct: 29.9 },
  { src: "/images/homepage/reliable/card-sandbox.png", topPct: 32.6 },
  { src: "/images/homepage/reliable/card-execution.png", topPct: 35.4 },
  { src: "/images/homepage/reliable/card-clock.png", topPct: 38.0 },
  { src: "/images/homepage/reliable/card-crypto.png", topPct: 40.7 },
  { src: "/images/homepage/reliable/card-shield.png", topPct: 43.3 },
  { src: "/images/homepage/reliable/card-output.png", topPct: 46.1 },
];

const TOTAL_STATES = FEATURES.length + 1;
const SEGMENT_SIZE = 1 / TOTAL_STATES;
/** Card 0 reveals its real image only after it has lifted most of the way */
const CARD_REVEAL_FRACTION = 0.85;
/** Stepped keyframes so text is centered exactly when each card finishes revealing */
const LEFT_COLUMN_Y_INPUT = Array.from({ length: TOTAL_STATES + 1 }, (_, i) =>
  i <= FEATURES.length ? i * SEGMENT_SIZE : 1,
);
const LEFT_COLUMN_Y_OUTPUT = Array.from(
  { length: TOTAL_STATES + 1 },
  (_, i) => `${-Math.min(i, FEATURES.length) * 100}%`,
);

function NumberBadge({ number }: { number: number }) {
  return (
    <div
      className="border-ds-neutral-200 bg-ds-surface-primary relative z-[5] flex h-14 w-14 shrink-0 items-center justify-center rounded-full border"
      style={{
        boxShadow:
          "0px 1px 2px rgba(0,0,0,0.06), 0px 2px 4px rgba(0,0,0,0.04), inset 0px -2px 4px rgba(0,0,0,0.04), inset 0px 2px 4px rgba(255,255,255,0.8)",
      }}
    >
      <div
        className="border-ds-neutral-300 flex h-10 w-10 items-center justify-center rounded-full border bg-white"
        style={{
          boxShadow: "inset 0px 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <span className="text-ds-text-secondary text-lg font-semibold">{number}</span>
      </div>
    </div>
  );
}

function LeftColumn({ scrollYProgress }: { scrollYProgress: MotionValue<number> }) {
  const y = useTransform(scrollYProgress, LEFT_COLUMN_Y_INPUT, LEFT_COLUMN_Y_OUTPUT);

  return (
    <div className="relative w-[38%] overflow-y-clip overflow-x-visible">
      <motion.div className="relative z-[5] h-full" style={{ y }}>
        {/* State 0: empty slot */}
        <div className="h-full flex-none" />
        {/* States 1-6 */}
        {FEATURES.map((feature, i) => (
          <div key={feature.title} className="flex h-full flex-none items-center">
            <div className="relative flex items-start gap-6 pr-6" style={{ marginLeft: "-19px" }}>
              <NumberBadge number={i + 1} />
              <div className="flex flex-col gap-3">
                <h3 className="text-ds-text-secondary leading-label text-3xl font-medium">{feature.title}</h3>
                <p className="text-ds-text-subtle/80 leading-label text-xl">{feature.description}</p>
              </div>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

function EngineCard({
  layer,
  index,
  scrollYProgress,
}: {
  layer: (typeof CARD_LAYERS)[number];
  index: number;
  scrollYProgress: MotionValue<number>;
}) {
  const liftStart = index * SEGMENT_SIZE;
  const liftEnd = (index + 1) * SEGMENT_SIZE;
  const isLast = index === CARD_LAYERS.length - 1;

  const y = useTransform(scrollYProgress, [liftStart, liftEnd], ["0%", "-70%"]);

  const revealStart = Math.max(0, (index - 1) * SEGMENT_SIZE);
  const revealEnd = Math.max(0, (index - 0.5) * SEGMENT_SIZE);
  const fadeOutStart = liftStart;
  const fadeOutEnd = (index + 0.5) * SEGMENT_SIZE;

  const opacityTransformer = useCallback(
    (v: number) => {
      if (index === 0) {
        const revealAt = liftEnd * CARD_REVEAL_FRACTION;
        if (v < revealAt) return 0;
        if (v < liftEnd) return (v - revealAt) / (liftEnd - revealAt);
        return 1;
      }
      if (v < revealStart) return 0;
      if (v < revealEnd) return (v - revealStart) / (revealEnd - revealStart);
      if (v < fadeOutStart) return 1;
      if (isLast) return 1;
      if (v < fadeOutEnd) return 1 - (v - fadeOutStart) / (fadeOutEnd - fadeOutStart);
      return 0;
    },
    [index, liftEnd, revealStart, revealEnd, fadeOutStart, fadeOutEnd, isLast],
  );
  const realOpacity = useTransform(scrollYProgress, opacityTransformer);

  const stackedSrc =
    index === 0 ? "/images/homepage/reliable/card-b3os-stacked.png" : "/images/homepage/reliable/card-stacked.png";

  return (
    <motion.div
      className="absolute"
      style={{
        top: `${layer.topPct}%`,
        left: "20%",
        width: "60%",
        aspectRatio: "1421 / 833",
        zIndex: CARD_LAYERS.length - index,
        ...(isLast ? {} : { y }),
      }}
    >
      <Image src={stackedSrc} alt="Stacked engine card background" fill className="object-contain" />
      <motion.div className="absolute inset-0" style={{ opacity: realOpacity }}>
        <Image
          src={layer.src}
          alt={`${FEATURES[index - 1]?.title ?? "B3OS engine"} feature card`}
          fill
          className="object-contain"
        />
      </motion.div>
    </motion.div>
  );
}

function RightColumn({ scrollYProgress }: { scrollYProgress: MotionValue<number> }) {
  return (
    <div className="relative w-[62%] overflow-hidden">
      {CARD_LAYERS.map((layer, index) => (
        <EngineCard key={layer.src} layer={layer} index={index} scrollYProgress={scrollYProgress} />
      ))}
    </div>
  );
}

/** Curve geometry constants */
const CURVE_R = 40;
const CURVE_K = CURVE_R * 0.5523; // cubic Bezier circular approximation
const CURVE_HEIGHT = CURVE_R * 3; // straight segment + two quarter-circle arcs
const BORDER_X = 9; // left grid border offset within 1440px container

/** Single continuous path: top center dot → curve left → vertical → curve right → bottom center dot */
function fullBluePath(w: number, h: number): string {
  const R = CURVE_R;
  const K = CURVE_K;
  const CH = CURVE_HEIGHT;
  const TOP_GAP = 12;
  return [
    // Top curve: center → left border
    `M ${w} ${TOP_GAP}`,
    `V ${CH - 2 * R}`,
    `C ${w} ${CH - 2 * R + K} ${w - R + K} ${CH - R} ${w - R} ${CH - R}`,
    `H ${R}`,
    `C ${R - K} ${CH - R} 0 ${CH - K} 0 ${CH}`,
    // Vertical at left border
    `V ${h - CH}`,
    // Bottom curve: left border → center
    `C 0 ${h - CH + K} ${R - K} ${h - CH + R} ${R} ${h - CH + R}`,
    `H ${w - R}`,
    `C ${w - R + K} ${h - CH + R} ${w} ${h - CH + R + K} ${w} ${h - CH + 2 * R}`,
    `V ${h}`,
  ].join(" ");
}

function SectionHeading({ large, headingTag = "h2" }: { large?: boolean; headingTag?: "h2" | "p" }) {
  const HeadingTag = headingTag;
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <p className="text-ds-alpha-shadow-30 font-mono text-base font-medium">
        <span>[</span>
        <span className="text-ds-brand-600">02</span>
        <span>/05] HOW IT WORKS</span>
      </p>
      <HeadingTag
        className={`text-ds-text-subtle leading-label text-4xl font-medium tracking-[-1.44px] ${large ? "md:text-7xl" : ""}`}
      >
        Your Flows Reliable, by Design.
      </HeadingTag>
    </div>
  );
}

export function ReliableByDesign() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const topDotRef = useRef<HTMLDivElement>(null);
  const bottomDotRef = useRef<HTMLDivElement>(null);
  const curveContainerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  const [geo, setGeo] = useState<{ topDotY: number; bottomDotY: number; curveWidth: number } | null>(null);

  useEffect(() => {
    const container = curveContainerRef.current;
    const topDot = topDotRef.current;
    const bottomDot = bottomDotRef.current;
    if (!container || !topDot || !bottomDot) return;

    const measure = () => {
      const cr = container.getBoundingClientRect();
      const tdr = topDot.getBoundingClientRect();
      const bdr = bottomDot.getBoundingClientRect();
      setGeo({
        topDotY: tdr.top + tdr.height / 2 - cr.top,
        bottomDotY: bdr.top + bdr.height / 2 - cr.top,
        curveWidth: tdr.left + tdr.width / 2 - cr.left - BORDER_X,
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    ro.observe(topDot);
    ro.observe(bottomDot);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      {/* Mobile layout — no parallax */}
      <div className="flex flex-col gap-12 px-6 py-20 lg:hidden">
        <SectionHeading headingTag="p" />
        {FEATURES.map((feature, i) => (
          <div key={feature.title} className="flex items-start gap-4">
            <NumberBadge number={i + 1} />
            <div className="flex flex-col gap-3">
              <h3 className="text-ds-text-secondary leading-label text-xl font-medium">{feature.title}</h3>
              <p className="text-ds-text-subtle/80 body-cozy text-base">{feature.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop layout — parallax scroll */}
      <GridBorderedSection>
        {/* Blue line curves — above grid border (z-[4]), above sticky deck (z-auto) */}
        <div className="pointer-events-none absolute inset-0 z-[4] hidden justify-center lg:flex">
          <div ref={curveContainerRef} className="relative h-full w-full max-w-[1440px]">
            {geo && (
              <svg
                className="absolute overflow-visible"
                style={{
                  top: geo.topDotY,
                  left: BORDER_X,
                  width: geo.curveWidth,
                  height: geo.bottomDotY - geo.topDotY,
                }}
                viewBox={`0 0 ${geo.curveWidth} ${geo.bottomDotY - geo.topDotY}`}
                fill="none"
              >
                <path d={fullBluePath(geo.curveWidth, geo.bottomDotY - geo.topDotY)} stroke="#2F43FF" strokeWidth="2" />
              </svg>
            )}
          </div>
        </div>

        <section
          ref={sectionRef}
          className="bg-ds-surface-primary relative hidden lg:block"
          style={{ height: `${TOTAL_STATES * 100}vh` }}
        >
          {/* Section heading — scrolls away naturally, above the blue line */}
          <div className="relative z-[3] flex items-center justify-center px-6 pb-[100px] pt-16">
            <div className="mx-auto w-full max-w-[1440px]">
              <SectionHeading large />
            </div>
          </div>
          <GridDivider variant={3} className="z-[3]" centerDotBlue centerDotRef={topDotRef} />

          {/* Deck: sticky two-column parallax area */}
          <div className="sticky top-[72px] z-[5] mx-auto flex h-[calc(100vh-72px)] w-full max-w-[1440px] overflow-y-clip overflow-x-visible">
            <LeftColumn scrollYProgress={scrollYProgress} />
            <RightColumn scrollYProgress={scrollYProgress} />
          </div>
        </section>

        {/* Bottom GridDivider — inside component so blue line can connect to it */}
        <GridDivider variant={3} centerDotBlue centerDotRef={bottomDotRef} />
        <GridBorderedSection className="h-[100px]" />
      </GridBorderedSection>
    </>
  );
}
